/**
 * Push Progress Tracker
 * ─────────────────────
 * Compact, read-only 7-stage tracker rendered inside Push Mode modals
 * (Ordering, Permit, Reschedule, Quick Assign, etc.) so users see real
 * job progress while they execute the next action — without leaving
 * the current context.
 *
 * Source-of-truth: derived purely from canonical job fields. No business
 * logic mutations. Re-renders automatically when the parent passes a
 * fresh `job` snapshot after a state commit.
 */
import { Check, Circle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Job } from "@/data/types";
import { useAppData } from "@/data/AppDataContext";
import { deriveBuildReviewStatus, type BuildReviewEvent } from "@/data/buildReviewTypes";

type StageState = "done" | "active" | "pending" | "blocked";

interface Stage {
  key: string;
  label: string;
  state: StageState;
  hint?: string;
}

interface Props {
  job: Job;
  /** Optional override to highlight the stage the current modal is acting on. */
  activeKey?: string;
  className?: string;
  /**
   * Called when an operator clicks a clickable (active or blocked) stage.
   * If omitted, stages are display-only. Stage keys match the canonical
   * 8-stage tracker (review · permit · materials · crew · schedule · qa ·
   * closeout · invoice).
   */
  onStageClick?: (key: string) => void;
}

function deriveStages(job: Job, buildReviewEvents: BuildReviewEvent[], activeKey?: string): Stage[] {
  // Build Review — always required after a sale, before permit/ordering.
  const jobReviewEvents = buildReviewEvents.filter((e) => e.jobId === job.id);
  const reviewStatus = deriveBuildReviewStatus(jobReviewEvents);
  const reviewDone = reviewStatus === "Approved";
  const reviewBlocked =
    reviewStatus === "On Hold — Sales Action Required" || reviewStatus === "Revision Required";
  const reviewActive = !reviewDone && !reviewBlocked;

  const permitDone =
    !job.permitRequired ||
    job.permitStatus === "Approved" ||
    job.permitStatus === "Not Required";
  const permitActive =
    reviewDone && job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required";
  const permitBlocked = job.permitStatus === "Revision Needed";

  const materialsDone =
    job.materialReadiness === "Loaded for Crew" ||
    job.materialReadiness === "Fully Received" ||
    job.warehouseStatus === "Staged";
  const materialsActive = reviewDone && !materialsDone && job.materialReadiness !== "Not Ordered";
  const materialsBlocked = job.materialReadiness === "Missing Items";

  const crewDone = job.assignmentType !== "Unassigned" && !!job.assignedSubcontractor;
  const crewActive = reviewDone && !crewDone;

  const scheduleDone = !!job.scheduledInstallDate;
  const scheduleActive = reviewDone && crewDone && !scheduleDone;

  const qaDone = !!job.qaSubmitted;
  const qaActive =
    scheduleDone && !qaDone && (job.currentStage === "In Progress" || job.currentStage === "QA Review");

  const closeoutDone = job.closeoutStatus === "Closed" || job.closeoutStatus === "Ready for Closeout";
  const closeoutActive = qaDone && !closeoutDone;

  const invoiceSent = job.financials?.invoices?.some(
    (inv) => inv.status === "sent" || inv.status === "paid",
  );
  const invoiceDone = !!invoiceSent;
  const invoiceActive = qaDone && !invoiceDone;

  // Stage keys downstream of Review — they should NEVER render as "active"
  // (or as a misleading "done") while Review itself is unmet. They appear as
  // "pending" (locked) so the tracker reinforces the pre-spend gate banner
  // instead of contradicting it.
  const downstreamOfReview = new Set(["permit", "materials", "crew", "schedule", "qa", "closeout", "invoice"]);
  const reviewGateOpen = reviewDone;

  const stage = (key: string, label: string, done: boolean, active: boolean, blocked = false): Stage => {
    let state: StageState = "pending";
    const isGated = !reviewGateOpen && downstreamOfReview.has(key);

    if (isGated) {
      // Locked behind Review — force pending regardless of activeKey or partial completion.
      state = "pending";
    } else if (blocked) {
      state = "blocked";
    } else if (done) {
      state = "done";
    } else if (active || activeKey === key) {
      state = "active";
    }
    return { key, label, state };
  };

  return [
    stage("review", "Review", reviewDone, reviewActive, reviewBlocked),
    stage("permit", "Permit", permitDone, permitActive, permitBlocked),
    stage("materials", "Materials", materialsDone, materialsActive, materialsBlocked),
    stage("crew", "Crew", crewDone, crewActive),
    stage("schedule", "Schedule", scheduleDone, scheduleActive),
    stage("qa", "QA", qaDone, qaActive),
    stage("closeout", "Closeout", closeoutDone, closeoutActive),
    stage("invoice", "Invoice", invoiceDone, invoiceActive),
  ];
}

export function PushProgressTracker({ job, activeKey, className, onStageClick }: Props) {
  const { buildReviewEvents } = useAppData();
  const stages = deriveStages(job, buildReviewEvents, activeKey);
  const completed = stages.filter((s) => s.state === "done").length;

  return (
    <div className={cn("rounded-md border border-border bg-muted/20 px-4 py-3", className)}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Job Progress
        </span>
        <span className="text-[10px] font-medium text-muted-foreground shrink-0">
          {completed} / {stages.length} complete
        </span>
      </div>
      <div className="grid grid-cols-4 gap-x-3 gap-y-3 sm:grid-cols-8">
        {stages.map((s) => {
          const clickable =
            !!onStageClick && (s.state === "active" || s.state === "blocked");
          const dotClasses = cn(
            "h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors",
            s.state === "done" && "bg-success text-success-foreground",
            s.state === "active" && "bg-primary text-primary-foreground ring-2 ring-primary/20",
            s.state === "blocked" && "bg-destructive text-destructive-foreground",
            s.state === "pending" && "bg-muted text-muted-foreground border border-border",
          );
          const labelClasses = cn(
            "w-full px-1 text-[10px] leading-tight text-center whitespace-normal break-words min-h-[2.5rem]",
            s.state === "done" && "text-success font-medium",
            s.state === "active" && "text-primary font-bold",
            s.state === "blocked" && "text-destructive font-bold",
            s.state === "pending" && "text-muted-foreground font-medium",
          );
          const dot = (
            <div className={dotClasses}>
              {s.state === "done" && <Check className="h-3.5 w-3.5" />}
              {s.state === "active" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {s.state === "blocked" && <Circle className="h-3 w-3 fill-current" />}
              {s.state === "pending" && <Circle className="h-2.5 w-2.5" />}
            </div>
          );

          if (clickable) {
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => onStageClick?.(s.key)}
                className="flex flex-col items-center gap-1.5 min-w-0 rounded-md p-1 -m-1 transition-all hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer group"
                title={`Open ${s.label}`}
                aria-label={`Open ${s.label}`}
              >
                <div className="group-hover:scale-110 transition-transform">{dot}</div>
                <span className={cn(labelClasses, "group-hover:underline")}>{s.label}</span>
              </button>
            );
          }

          return (
            <div key={s.key} className="flex flex-col items-center gap-1.5 min-w-0">
              {dot}
              <span className={labelClasses}>{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
